import { QueryClient } from '@tanstack/react-query'
import { Address } from 'viem'

export interface InvalidateBalancesCacheParams {
  queryClient: QueryClient
  chainId: number
  /** User's wallet address (optional, ALL balances are invalidated regardless) */
  address?: Address
  /** Sub-account address (position) - optional */
  position?: Address
  /** Specific vault address to invalidate - optional */
  vaultAddress?: Address
  /** Additional sub-account address to invalidate (e.g., for repay operations) - optional */
  subAccountAddress?: Address
}

export function getWagmiReadQueryKeys(): Array<readonly [string]> {
  return [['readContract'], ['readContracts']]
}

/**
 * Centralized function to invalidate all balance-related caches after transactions.
 * This ensures consistent cache invalidation across all eLend, eBorrow, and eVault operations.
 *
 * Call this after successful transactions to refresh:
 * - Wallet balances (both ERC20 and native)
 * - Vault balances and data
 * - Account positions
 * - eLend/eBorrow specific data
 */
export function invalidateBalancesCache({
  queryClient,
  chainId,
  address: _address,
  position: _position,
  vaultAddress,
  subAccountAddress: _subAccountAddress,
}: InvalidateBalancesCacheParams): void {
  // === Wagmi native balance (useBalance hook) ===
  // Invalidate ALL native token balance queries (HYPE, ETH, etc.)
  void queryClient.invalidateQueries({
    queryKey: ['balance'],
    refetchType: 'all',
  })

  // === Wallet Token Balances (ALL - for swaps that affect multiple tokens) ===
  void queryClient.invalidateQueries({
    queryKey: ['walletTokenBalances'],
    refetchType: 'all',
  })

  // === Generic balances (ALL) ===
  void queryClient.invalidateQueries({
    queryKey: ['balances'],
    refetchType: 'all',
  })

  // === Account positions (ALL) ===
  void queryClient.invalidateQueries({
    queryKey: ['accountPositions'],
    refetchType: 'all',
  })

  // === eBorrow positions (ALL) ===
  void queryClient.invalidateQueries({
    queryKey: ['eBorrowPositions'],
    refetchType: 'all',
  })

  // === Account controller (ALL) ===
  void queryClient.invalidateQueries({
    queryKey: ['accountController'],
    refetchType: 'all',
  })

  // === Vault-specific data ===
  if (vaultAddress) {
    void queryClient.invalidateQueries({
      queryKey: ['evault', chainId, vaultAddress],
      refetchType: 'all',
    })
  }
  // Also invalidate all evault queries for swaps
  void queryClient.invalidateQueries({
    queryKey: ['evault'],
    refetchType: 'all',
  })

  // === Global read contract queries (wagmi) ===
  for (const queryKey of getWagmiReadQueryKeys()) {
    void queryClient.invalidateQueries({
      queryKey,
      refetchType: 'all',
    })
  }

  // === eLend specific ===
  void queryClient.invalidateQueries({
    queryKey: ['elends'],
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: ['eLendAccountData'],
    refetchType: 'all',
  })

  // === Pooled/Aave markets (reserves) ===
  void queryClient.invalidateQueries({
    queryKey: ['reserves'],
    refetchType: 'all',
  })

  // === Euler Indexer EVaults (used by EVault cards on main page) ===
  void queryClient.invalidateQueries({
    queryKey: ['euler', 'evaults'],
    refetchType: 'all',
  })

  // === Immediate refetch of critical queries ===
  void queryClient.refetchQueries({
    queryKey: ['walletTokenBalances'],
  })
  void queryClient.refetchQueries({
    queryKey: ['balance'],
  })
  for (const queryKey of getWagmiReadQueryKeys()) {
    void queryClient.refetchQueries({ queryKey })
  }
  void queryClient.refetchQueries({
    queryKey: ['reserves'],
  })
}

/**
 * Hook-friendly version that returns a callback to invalidate caches.
 * Use this in React components with useCallback to memoize.
 */
export function createInvalidateBalancesCache(queryClient: QueryClient) {
  return (params: Omit<InvalidateBalancesCacheParams, 'queryClient'>) => {
    invalidateBalancesCache({ ...params, queryClient })
  }
}
